import { useState, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { aiApi } from '@/api/ai';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { DateRangePicker } from '@/components/ui/date-picker';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabPanel } from '@/components/ui/tabs';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { format, subDays } from 'date-fns';
import type { AIAnalysisResult, AnalysisType, InsightPriority, Difficulty } from '@/types/ai';

const analysisTypes: { value: AnalysisType; label: string }[] = [
  { value: 'monthly_summary', label: '月次サマリー' },
  { value: 'funnel_analysis', label: 'ファネル分析' },
  { value: 'trend_analysis', label: 'トレンド分析' },
  { value: 'comparative', label: '比較分析' },
];

const priorityColors: Record<InsightPriority, 'danger' | 'warning' | 'info'> = {
  high: 'danger',
  medium: 'warning',
  low: 'info',
};

const priorityLabels: Record<InsightPriority, string> = {
  high: '高',
  medium: '中',
  low: '低',
};

const difficultyColors: Record<Difficulty, 'success' | 'warning' | 'danger'> = {
  easy: 'success',
  medium: 'warning',
  hard: 'danger',
};

const difficultyLabels: Record<Difficulty, string> = {
  easy: '簡単',
  medium: '普通',
  hard: '難しい',
};

export default function AIAnalysis() {
  const { accessibleCompanies } = useAuthStore();
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [analysisType, setAnalysisType] = useState<AnalysisType>('monthly_summary');
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<AIAnalysisResult | null>(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [error, setError] = useState<string | null>(null);

  // 初期法人選択
  useEffect(() => {
    if (accessibleCompanies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(accessibleCompanies[0].id);
    }
  }, [accessibleCompanies, selectedCompanyId]);

  const handleAnalyze = async () => {
    if (!selectedCompanyId) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await aiApi.analyze({
        company_id: selectedCompanyId,
        analysis_type: analysisType,
        period_start: startDate,
        period_end: endDate,
      });
      setResult(data);
      setActiveTab('summary');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'AI分析に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const companyOptions = accessibleCompanies.map((c) => ({
    value: c.id,
    label: c.display_name,
  }));

  const tabs = [
    { id: 'summary', label: 'サマリー' },
    { id: 'insights', label: 'インサイト' },
    { id: 'recommendations', label: '改善提案' },
    { id: 'findings', label: '重要な発見' },
  ];

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">AI分析</h1>
        <p className="text-sm sm:text-base text-gray-500">
          AIによるデータ分析と改善提案を確認できます
        </p>
      </div>

      {/* 分析条件 */}
      <Card>
        <CardHeader>
          <CardTitle>分析条件</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* 法人・分析タイプ */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label="法人"
                options={companyOptions}
                value={selectedCompanyId?.toString() || ''}
                onChange={(value) => setSelectedCompanyId(Number(value))}
              />
              <Select
                label="分析タイプ"
                options={analysisTypes}
                value={analysisType}
                onChange={(value) => setAnalysisType(value as AnalysisType)}
              />
            </div>

            {/* 期間・ボタン */}
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex-1">
                <DateRangePicker
                  label="期間"
                  startDate={startDate}
                  endDate={endDate}
                  onStartDateChange={setStartDate}
                  onEndDateChange={setEndDate}
                />
              </div>
              <Button
                onClick={handleAnalyze}
                isLoading={isLoading}
                disabled={!selectedCompanyId}
                className="w-full sm:w-auto shrink-0"
              >
                分析実行
              </Button>
            </div>
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{error}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 分析結果 */}
      {isLoading ? (
        <Card>
          <CardContent className="py-12">
            <div className="flex flex-col items-center justify-center">
              <LoadingSpinner />
              <p className="mt-4 text-gray-500">AI分析を実行中...</p>
            </div>
          </CardContent>
        </Card>
      ) : result ? (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>分析結果</CardTitle>
              <div className="flex items-center gap-2">
                {result.is_cached && (
                  <Badge variant="info">キャッシュ</Badge>
                )}
                <span className="text-sm text-gray-500">
                  {format(new Date(result.created_at), 'yyyy/MM/dd HH:mm')}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

            {/* サマリータブ */}
            <TabPanel id="summary" activeTab={activeTab}>
              <div className="prose max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap">{result.summary}</p>
              </div>
            </TabPanel>

            {/* インサイトタブ */}
            <TabPanel id="insights" activeTab={activeTab}>
              <div className="space-y-4">
                {result.insights.length > 0 ? (
                  result.insights.map((insight, index) => (
                    <div
                      key={index}
                      className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-start justify-between">
                        <h4 className="font-semibold text-gray-900">{insight.title}</h4>
                        <Badge variant={priorityColors[insight.priority]}>
                          優先度: {priorityLabels[insight.priority]}
                        </Badge>
                      </div>
                      <p className="mt-2 text-gray-600">{insight.description}</p>
                      {insight.metric_impact && (
                        <p className="mt-2 text-sm text-gray-500">
                          影響指標: {insight.metric_impact}
                        </p>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500">インサイトがありません</p>
                )}
              </div>
            </TabPanel>

            {/* 改善提案タブ */}
            <TabPanel id="recommendations" activeTab={activeTab}>
              <div className="space-y-4">
                {result.recommendations.length > 0 ? (
                  result.recommendations.map((rec, index) => (
                    <div
                      key={index}
                      className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-start justify-between">
                        <h4 className="font-semibold text-gray-900">
                          {index + 1}. {rec.action}
                        </h4>
                        <div className="flex gap-2">
                          <Badge variant={difficultyColors[rec.difficulty]}>
                            難易度: {difficultyLabels[rec.difficulty]}
                          </Badge>
                          <Badge variant="default">
                            優先度: {rec.priority}
                          </Badge>
                        </div>
                      </div>
                      <p className="mt-2 text-gray-600">{rec.rationale}</p>
                      <p className="mt-2 text-sm text-green-600">
                        期待効果: {rec.expected_impact}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500">改善提案がありません</p>
                )}
              </div>
            </TabPanel>

            {/* 重要な発見タブ */}
            <TabPanel id="findings" activeTab={activeTab}>
              <div className="space-y-2">
                {result.key_findings.length > 0 ? (
                  <ul className="list-disc list-inside space-y-2">
                    {result.key_findings.map((finding, index) => (
                      <li key={index} className="text-gray-700">
                        {finding}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500">重要な発見がありません</p>
                )}
              </div>
            </TabPanel>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-gray-500">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
              <p className="mt-4">分析条件を設定して「分析実行」をクリックしてください</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
